import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export function flattenUgpNodes(enrollment) {
  const nodes = [];
  const ugp = enrollment.ugp;
  if (enrollment.student_info) {
    nodes.push({
      key: `si-${enrollment.student_info.id}`,
      table: 'student_info',
      id: enrollment.student_info.id,
      label: enrollment.student_info.status || 'Student info',
      sublabel: enrollment.student_info.grade_level || 'record'
    });
  }
  (enrollment.credentials_keys || []).forEach((c) => {
    nodes.push({
      key: `ck-${c.id}`,
      table: 'credentials_keys',
      id: c.id,
      label: c.platform_label || 'Credential',
      sublabel: [c.entry_type, c.source_slug].filter(Boolean).join(' · ')
    });
  });
  (enrollment.documents || []).forEach((d) => {
    nodes.push({
      key: `doc-${d.id}`,
      table: 'documents',
      id: d.id,
      label: d.title || 'Document',
      sublabel: [d.category, d.source_slug].filter(Boolean).join(' · ')
    });
  });
  (enrollment.person_links || []).forEach((p) => {
    nodes.push({
      key: `pl-${p.relationship_id}`,
      table: 'relationships',
      id: p.relationship_id,
      label: p.person_label || p.relationship_type,
      sublabel: p.relationship_type
    });
  });
  (enrollment.form_submissions || []).forEach((f) => {
    nodes.push({
      key: `fs-${f.id}`,
      table: 'form_submissions',
      id: f.id,
      label: f.form_title || 'Submission',
      sublabel: 'form'
    });
  });
  return { ugp, nodes };
}

function layoutOrgHierarchy(orgTree, viewportWidth, showIds) {
  const teams = orgTree?.teams || [];
  const org = orgTree?.org;
  const orgH = showIds ? 72 : 56;
  const teamH = showIds ? 52 : 44;
  const ugpH = showIds ? 40 : 34;
  const leafH = showIds ? 36 : 30;
  const leafW = 108;
  const leafGap = 6;
  const teamW = 156;
  const ugpW = 132;
  const orgW = 200;
  const padX = 48;
  const padY = 40;

  const baseWidth = Math.max(viewportWidth || 880, 400);
  const colW = Math.max(200, Math.min(280, (baseWidth - padX * 2) / Math.max(teams.length, 1)));
  const orgX = baseWidth / 2;
  const orgY = padY + orgH / 2;
  const teamY = orgY + orgH / 2 + 36 + teamH / 2;

  let maxBottom = orgH + padY;
  let minX = orgX - orgW / 2;
  let maxX = orgX + orgW / 2;
  let minY = orgY - orgH / 2;

  const bump = (x, y, halfW, halfH) => {
    minX = Math.min(minX, x - halfW);
    maxX = Math.max(maxX, x + halfW);
    minY = Math.min(minY, y - halfH);
    maxBottom = Math.max(maxBottom, y + halfH);
  };

  const columns = teams.map((team, colIndex) => {
    const cx =
      teams.length === 1
        ? orgX
        : (baseWidth - teams.length * colW) / 2 + colW / 2 + colIndex * colW;
    bump(cx, teamY, teamW / 2, teamH / 2);
    let y = teamY + teamH / 2 + 20;
    const ugpBlocks = (team.enrollments || []).map((enrollment) => {
      const { nodes } = flattenUgpNodes(enrollment);
      const ugpCy = y + ugpH / 2;
      bump(cx, ugpCy, ugpW / 2, ugpH / 2);
      y += ugpH + 8;
      const leafCount = nodes.length;
      const leafRowW = leafCount > 0 ? leafCount * leafW + (leafCount - 1) * leafGap : 0;
      const leafStartX = cx - leafRowW / 2 + leafW / 2;
      const leafPositions = nodes.map((node, li) => {
        const lx = leafStartX + li * (leafW + leafGap);
        const ly = y + leafH / 2;
        bump(lx, ly, leafW / 2, leafH / 2);
        return { node, x: lx, y: ly };
      });
      y += leafCount > 0 ? leafH + 14 : 6;
      return { enrollment, ugp: { x: cx, y: ugpCy }, leafPositions };
    });
    return { team, teamCenter: { x: cx, y: teamY }, ugpBlocks };
  });

  const contentW = maxX - minX;
  const contentH = maxBottom - minY;
  const width = Math.max(baseWidth, contentW + padX * 2);
  const height = Math.max(420, contentH + padY * 2);
  const offsetX = (width - contentW) / 2 - minX;
  const offsetY = padY - minY;

  const shift = (p) => ({ x: p.x + offsetX, y: p.y + offsetY });

  return {
    org: {
      ...shift({ x: orgX, y: orgY }),
      label: org?.label || org?.group_name,
      id: org?.id
    },
    orgW,
    orgH,
    teamW,
    teamH,
    ugpW,
    ugpH,
    leafW,
    leafH,
    columns: columns.map((col) => ({
      ...col,
      teamCenter: shift(col.teamCenter),
      ugpBlocks: col.ugpBlocks.map((block) => ({
        ...block,
        ugp: shift(block.ugp),
        leafPositions: block.leafPositions.map((lp) => ({ ...lp, ...shift(lp) }))
      }))
    })),
    height,
    width
  };
}

const truncate = (s, n) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

const formatDbId = (id) => {
  const s = String(id);
  return s.length > 22 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
};

/** Delete control lives inside the node’s hover group so moving to × does not clear hover. */
function WebDeleteButton({ onDelete }) {
  const stop = (e) => e.stopPropagation();
  return (
    <g
      transform="translate(0, 0)"
      style={{ cursor: 'pointer' }}
      onPointerDown={stop}
      onClick={(e) => {
        stop(e);
        onDelete(e);
      }}
    >
      <circle r={18} fill="transparent" pointerEvents="all" />
      <circle r={10} fill="#ffffff" stroke="#ef4444" strokeWidth={1.2} pointerEvents="none" />
      <line x1={-4} y1={-4} x2={4} y2={4} stroke="#ef4444" strokeWidth={1.4} strokeLinecap="round" pointerEvents="none" />
      <line x1={-4} y1={4} x2={4} y2={-4} stroke="#ef4444" strokeWidth={1.4} strokeLinecap="round" pointerEvents="none" />
    </g>
  );
}

const NODE_COLORS = {
  groups_periods: { fill: '#0f172a', stroke: '#0f172a', text: '#ffffff', sub: '#cbd5e1' },
  student_info: { fill: '#eff6ff', stroke: '#3b82f6', text: '#1e3a8a', sub: '#3b82f6' },
  credentials_keys: { fill: '#fffbeb', stroke: '#d97706', text: '#78350f', sub: '#b45309' },
  documents: { fill: '#ecfdf5', stroke: '#059669', text: '#064e3b', sub: '#047857' },
  person_links: { fill: '#f5f3ff', stroke: '#7c3aed', text: '#4c1d95', sub: '#6d28d9' },
  form_submissions: { fill: '#fff7ed', stroke: '#ea580c', text: '#7c2d12', sub: '#c2410c' },
  gp_relationship: { fill: '#f8fafc', stroke: '#94a3b8', text: '#334155', sub: '#64748b' },
  orphan: { fill: '#fffbeb', stroke: '#d97706', text: '#92400e', sub: '#b45309' }
};

function layoutOrphanStrip(orphans, baseLayout, showIds) {
  const list = orphans || [];
  if (!list.length) {
    return { ...baseLayout, orphans: [], orphanBand: null };
  }

  const nodeW = showIds ? 168 : 148;
  const nodeH = showIds ? 54 : 44;
  const gap = 10;
  const padX = 48;
  const bandTop = baseLayout.height + 28;
  const titleH = 22;
  const cols = Math.max(1, Math.floor((Math.max(baseLayout.width, 640) - padX * 2 + gap) / (nodeW + gap)));
  const rows = Math.ceil(list.length / cols);
  const bandW = Math.max(baseLayout.width, padX * 2 + cols * nodeW + (cols - 1) * gap);
  const bandH = titleH + 8 + rows * nodeH + (rows - 1) * gap + 24;

  const positions = list.map((rel, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = padX + col * (nodeW + gap) + nodeW / 2;
    const y = bandTop + titleH + 8 + row * (nodeH + gap) + nodeH / 2;
    return { rel, x, y, w: nodeW, h: nodeH };
  });

  return {
    ...baseLayout,
    width: Math.max(baseLayout.width, bandW),
    height: bandTop + bandH,
    orphans: positions,
    orphanBand: { y: bandTop, titleH, count: list.length }
  };
}

export function OrgHierarchyWeb({
  orgTree,
  orphans = [],
  showIds,
  onDeleteTeamGp,
  onDeleteUgp,
  onDeleteEntity,
  styles,
  fontStack,
  monoFont
}) {
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(880);
  const [hoverKey, setHoverKey] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setViewportWidth(containerRef.current.clientWidth || 880);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const layout = useMemo(() => {
    const base = layoutOrgHierarchy(orgTree, viewportWidth, showIds);
    return layoutOrphanStrip(orphans, base, showIds);
  }, [orgTree, orphans, viewportWidth, showIds]);
  const MIN_ZOOM = 0.15;
  const MAX_ZOOM = 3;

  const fitToView = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp || layout.width < 1) return;
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    const pad = 24;
    let z = Math.min((vw - pad * 2) / layout.width, (vh - pad * 2) / layout.height);
    if (!Number.isFinite(z)) return;
    if (z > 1) z = 1;
    z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
    setZoom(z);
    setPan({ x: (vw - layout.width * z) / 2, y: (vh - layout.height * z) / 2 });
  }, [layout.width, layout.height]);

  useLayoutEffect(() => {
    fitToView();
  }, [fitToView, orgTree, orphans, showIds]);

  const nudgeZoom = useCallback((delta, anchor) => {
    setZoom((zOld) => {
      const zNew = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zOld * (1 + delta)));
      if (anchor) {
        const ratio = zNew / zOld;
        setPan((p) => ({
          x: anchor.x - (anchor.x - p.x) * ratio,
          y: anchor.y - (anchor.y - p.y) * ratio
        }));
      }
      return zNew;
    });
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      dragRef.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y, moved: false };
    },
    [pan.x, pan.y]
  );

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) > 5) {
      d.moved = true;
      setIsPanning(true);
      try {
        viewportRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (d.moved) setPan({ x: d.px + dx, y: d.py + dy });
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    dragRef.current = null;
    try {
      viewportRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    setIsPanning(false);
  }, []);

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();
      const vp = viewportRef.current;
      if (!vp) return;
      const rect = vp.getBoundingClientRect();
      const factor = e.deltaY > 0 ? -0.08 : 0.08;
      nudgeZoom(factor, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    },
    [nudgeZoom]
  );

  const orgTitle = truncate(layout.org.label || 'Organization', 26);

  const bindNodeHover = useCallback(
    (key) => ({
      onMouseEnter: () => setHoverKey(key),
      onMouseLeave: () => setHoverKey((h) => (h === key ? null : h))
    }),
    []
  );

  const orphanPalette = NODE_COLORS.orphan;

  return (
    <div ref={containerRef} style={styles.webWrap}>
      <div style={styles.webCanvasToolbar}>
        <div style={styles.webCanvasToolCluster}>
          <button
            type="button"
            style={styles.webCanvasToolBtn}
            title="Zoom out"
            onClick={() => {
              const vp = viewportRef.current;
              const anchor = vp ? { x: vp.clientWidth / 2, y: vp.clientHeight / 2 } : null;
              nudgeZoom(-0.08, anchor);
            }}
          >
            -
          </button>
          <button
            type="button"
            style={{ ...styles.webCanvasToolBtn, ...styles.webCanvasToolBtnEnd }}
            title="Zoom in"
            onClick={() => {
              const vp = viewportRef.current;
              const anchor = vp ? { x: vp.clientWidth / 2, y: vp.clientHeight / 2 } : null;
              nudgeZoom(0.08, anchor);
            }}
          >
            +
          </button>
        </div>
        <button type="button" style={styles.webCanvasToolBtnFit} onClick={fitToView} title="Fit map">
          Fit
        </button>
        {(orphans || []).length > 0 && (
          <span style={{ ...styles.webCanvasHint, marginLeft: 8, color: '#b45309' }}>
            {orphans.length} orphan rel{orphans.length === 1 ? '' : 's'}
          </span>
        )}
        <span style={styles.webCanvasHint}>Drag to pan · wheel to zoom · hover × to delete</span>
      </div>
      <div
        ref={viewportRef}
        style={{ ...styles.webCanvasViewport, ...(isPanning ? { cursor: 'grabbing' } : {}) }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
          <svg
            width={layout.width}
            height={layout.height}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            style={styles.webSvg}
            overflow="visible"
          >
            {layout.columns.map((col) => {
              const team = col.team;
              const teamKey = `team-${team.gp?.id}`;
              const teamHover = hoverKey === teamKey;
              const teamLabel = truncate(team.gp?.title || team.gp?.group_name || 'Team', 20);
              const periodSub = truncate(team.gp?.period_title || team.gp?.period_code || '', 18);
              return (
                <g key={team.gp?.id}>
                  <line x1={layout.org.x} y1={layout.org.y + layout.orgH / 2} x2={col.teamCenter.x} y2={col.teamCenter.y - layout.teamH / 2} stroke="#cbd5e1" strokeWidth={1} />
                  <g
                    transform={`translate(${col.teamCenter.x - layout.teamW / 2}, ${col.teamCenter.y - layout.teamH / 2})`}
                    {...bindNodeHover(teamKey)}
                  >
                    <rect
                      x={-6}
                      y={-14}
                      width={layout.teamW + 12}
                      height={layout.teamH + 18}
                      fill="transparent"
                      pointerEvents="all"
                    />
                    <rect
                      width={layout.teamW}
                      height={layout.teamH}
                      rx={8}
                      fill={teamHover ? '#e0e7ff' : '#eef2ff'}
                      stroke="#6366f1"
                      strokeWidth={teamHover ? 1.6 : 1.2}
                      pointerEvents="none"
                    />
                    <text x={layout.teamW / 2} y={layout.teamH / 2 - (showIds ? 6 : 2)} textAnchor="middle" fontSize="11" fontWeight="600" fill="#312e81" fontFamily={fontStack} pointerEvents="none">{teamLabel}</text>
                    <text x={layout.teamW / 2} y={layout.teamH / 2 + (showIds ? 4 : 10)} textAnchor="middle" fontSize="9" fill="#64748b" fontFamily={fontStack} pointerEvents="none">{periodSub}</text>
                    {showIds && (
                      <text x={layout.teamW / 2} y={layout.teamH / 2 + 16} textAnchor="middle" fontSize="7" fill="#94a3b8" fontFamily={monoFont} pointerEvents="none">{truncate(String(team.gp?.id || ''), 18)}</text>
                    )}
                    {teamHover && (
                      <g transform={`translate(${layout.teamW - 10}, -10)`}>
                        <WebDeleteButton onDelete={() => onDeleteTeamGp(team.gp.id, teamLabel)} />
                      </g>
                    )}
                  </g>
                  {col.ugpBlocks.map((block) => {
                    const ugp = block.enrollment.ugp;
                    const ugpKey = `ugp-${ugp.id}`;
                    const ugpHover = hoverKey === ugpKey;
                    const ugpLabel = truncate(ugp.user_name || 'Member', 18);
                    return (
                      <g key={ugp.id}>
                        <line x1={col.teamCenter.x} y1={col.teamCenter.y + layout.teamH / 2} x2={block.ugp.x} y2={block.ugp.y - layout.ugpH / 2} stroke="#dbe3ee" strokeWidth={1} />
                        <g
                          transform={`translate(${block.ugp.x - layout.ugpW / 2}, ${block.ugp.y - layout.ugpH / 2})`}
                          {...bindNodeHover(ugpKey)}
                        >
                          <rect x={-6} y={-14} width={layout.ugpW + 12} height={layout.ugpH + 18} fill="transparent" pointerEvents="all" />
                          <rect
                            width={layout.ugpW}
                            height={layout.ugpH}
                            rx={6}
                            fill={ugpHover ? '#f8fafc' : '#fff'}
                            stroke={ugpHover ? '#64748b' : '#cbd5e1'}
                            strokeWidth={ugpHover ? 1.4 : 1}
                            pointerEvents="none"
                          />
                          <text x={layout.ugpW / 2} y={layout.ugpH / 2 + (showIds ? -2 : 3)} textAnchor="middle" fontSize="10" fontWeight="500" fill="#0f172a" fontFamily={fontStack} pointerEvents="none">{ugpLabel}</text>
                          {showIds && (
                            <text x={layout.ugpW / 2} y={layout.ugpH / 2 + 12} textAnchor="middle" fontSize="7" fill="#94a3b8" fontFamily={monoFont} pointerEvents="none">{truncate(String(ugp.id), 16)}</text>
                          )}
                          {ugpHover && (
                            <g transform={`translate(${layout.ugpW - 10}, -10)`}>
                              <WebDeleteButton onDelete={() => onDeleteUgp(ugp.id, ugp.user_name)} />
                            </g>
                          )}
                        </g>
                        {block.leafPositions.map((lp) => {
                          const palette = NODE_COLORS[lp.node.table] || NODE_COLORS.gp_relationship;
                          const leafKey = lp.node.key;
                          const leafHover = hoverKey === leafKey;
                          return (
                            <g key={leafKey}>
                              <line x1={block.ugp.x} y1={block.ugp.y + layout.ugpH / 2} x2={lp.x} y2={lp.y - layout.leafH / 2} stroke="#e2e8f0" strokeWidth={1} strokeDasharray="2 3" />
                              <g
                                transform={`translate(${lp.x - layout.leafW / 2}, ${lp.y - layout.leafH / 2})`}
                                {...bindNodeHover(leafKey)}
                              >
                                <rect x={-6} y={-14} width={layout.leafW + 12} height={layout.leafH + 18} fill="transparent" pointerEvents="all" />
                                <rect
                                  width={layout.leafW}
                                  height={layout.leafH}
                                  rx={5}
                                  fill={palette.fill}
                                  stroke={palette.stroke}
                                  strokeWidth={leafHover ? 1.4 : 1}
                                  pointerEvents="none"
                                />
                                <text x={layout.leafW / 2} y={layout.leafH / 2 - 2} textAnchor="middle" fontSize="8" fontWeight="600" fill={palette.text} fontFamily={fontStack} pointerEvents="none">{truncate(lp.node.label, 14)}</text>
                                <text x={layout.leafW / 2} y={layout.leafH / 2 + 9} textAnchor="middle" fontSize="7" fill={palette.sub} fontFamily={fontStack} pointerEvents="none">{lp.node.table}</text>
                                {leafHover && (
                                  <g transform={`translate(${layout.leafW - 10}, -10)`}>
                                    <WebDeleteButton
                                      onDelete={() => onDeleteEntity(lp.node.table, lp.node.id, lp.node.label)}
                                    />
                                  </g>
                                )}
                              </g>
                            </g>
                          );
                        })}
                      </g>
                    );
                  })}
                </g>
              );
            })}
            <g transform={`translate(${layout.org.x - layout.orgW / 2}, ${layout.org.y - layout.orgH / 2})`}>
              <rect width={layout.orgW} height={layout.orgH} rx={10} fill={NODE_COLORS.groups_periods.fill} />
              <text x={layout.orgW / 2} y={layout.orgH / 2 - 6} textAnchor="middle" fontSize="13" fontWeight="600" fill="#fff" fontFamily={fontStack}>{orgTitle}</text>
              <text x={layout.orgW / 2} y={layout.orgH / 2 + 10} textAnchor="middle" fontSize="9" fill="#cbd5e1" fontFamily={fontStack}>Organization · Permanent</text>
            </g>

            {layout.orphanBand && (
              <g>
                <line
                  x1={24}
                  y1={layout.orphanBand.y - 12}
                  x2={layout.width - 24}
                  y2={layout.orphanBand.y - 12}
                  stroke="#fcd34d"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
                <text
                  x={32}
                  y={layout.orphanBand.y + 14}
                  fontSize="11"
                  fontWeight="700"
                  fill="#92400e"
                  fontFamily={fontStack}
                >
                  Orphan / missing refs ({layout.orphanBand.count}) — target rows gone
                </text>
                {(layout.orphans || []).map(({ rel, x, y, w, h }) => {
                  const key = `orphan-${rel.id}`;
                  const hover = hoverKey === key;
                  const missing = (rel.missing_sides || []).join(', ') || 'missing target';
                  const edge = `${rel.parent_table} → ${rel.child_table}`;
                  return (
                    <g key={key}>
                      <g
                        transform={`translate(${x - w / 2}, ${y - h / 2})`}
                        {...bindNodeHover(key)}
                      >
                        <rect x={-6} y={-14} width={w + 12} height={h + 18} fill="transparent" pointerEvents="all" />
                        <rect
                          width={w}
                          height={h}
                          rx={6}
                          fill={hover ? '#fef3c7' : orphanPalette.fill}
                          stroke={orphanPalette.stroke}
                          strokeWidth={hover ? 1.6 : 1.2}
                          strokeDasharray="3 2"
                          pointerEvents="none"
                        />
                        <text
                          x={w / 2}
                          y={14}
                          textAnchor="middle"
                          fontSize="9"
                          fontWeight="700"
                          fill={orphanPalette.text}
                          fontFamily={fontStack}
                          pointerEvents="none"
                        >
                          {truncate(rel.relationship_type || 'orphan', 18)}
                        </text>
                        <text
                          x={w / 2}
                          y={27}
                          textAnchor="middle"
                          fontSize="7"
                          fill={orphanPalette.sub}
                          fontFamily={fontStack}
                          pointerEvents="none"
                        >
                          {truncate(edge, 22)}
                        </text>
                        <text
                          x={w / 2}
                          y={38}
                          textAnchor="middle"
                          fontSize="7"
                          fill="#b45309"
                          fontFamily={fontStack}
                          pointerEvents="none"
                        >
                          {truncate(missing, 22)}
                        </text>
                        {showIds && (
                          <text
                            x={w / 2}
                            y={h - 4}
                            textAnchor="middle"
                            fontSize="6"
                            fill="#94a3b8"
                            fontFamily={monoFont}
                            pointerEvents="none"
                          >
                            {truncate(String(rel.id), 20)}
                          </text>
                        )}
                        {hover && (
                          <g transform={`translate(${w - 10}, -10)`}>
                            <WebDeleteButton
                              onDelete={() => {
                                const table = rel._deleteTable || 'relationships';
                                const label = rel.relationship_type || 'orphan';
                                if (table === 'groups_periods' && onDeleteTeamGp) {
                                  onDeleteTeamGp(rel.id, label);
                                } else if (table === 'user_groups_periods' && onDeleteUgp) {
                                  onDeleteUgp(rel.id, label);
                                } else {
                                  onDeleteEntity(table, rel.id, label);
                                }
                              }}
                            />
                          </g>
                        )}
                      </g>
                    </g>
                  );
                })}
              </g>
            )}
          </svg>
        </div>
      </div>
      {layout.columns.length === 0 && !(layout.orphans || []).length && (
        <div style={styles.emptyWebHint}>No team group-periods linked under this org (team_nested in Relationships).</div>
      )}
    </div>
  );
}

export function OrgHierarchyList({ orgTree, onDeleteTeamGp, onDeleteUgp, onDeleteEntity, styles, colors }) {
  const teams = orgTree?.teams || [];
  if (!teams.length) {
    return <div style={styles.emptyState}>No teams under this organization.</div>;
  }
  return (
    <div style={styles.listStack}>
      {teams.map((team) => {
        const teamLabel = team.gp?.title || team.gp?.group_name || 'Team';
        return (
          <div key={team.gp?.id} style={styles.listUgpBlock}>
            <div style={styles.listUgpHead}>
              <div>
                <div style={styles.regStudent}>{teamLabel}</div>
                <div style={styles.muted}>
                  {[team.gp?.period_title, team.gp?.period_code].filter(Boolean).join(' · ')} · {team.counts?.enrollments ?? 0} members
                </div>
              </div>
              <button type="button" style={styles.dangerButton} onClick={() => onDeleteTeamGp(team.gp.id, teamLabel)}>Delete team GP</button>
            </div>
            {(team.enrollments || []).map((e) => {
              const { ugp, nodes } = flattenUgpNodes(e);
              return (
                <div key={ugp.id} style={{ borderTop: `1px solid ${colors.border}`, padding: '8px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.82rem' }}>{ugp.user_name}</div>
                      <div style={styles.muted}>{ugp.email}</div>
                    </div>
                    <button type="button" style={styles.dangerButton} onClick={() => onDeleteUgp(ugp.id, ugp.user_name)}>Delete enrollment</button>
                  </div>
                  {nodes.map((n) => (
                    <div key={n.key} style={styles.listLeafRow}>
                      <div><span style={styles.tag}>{n.table}</span> {n.label}</div>
                      <button type="button" style={styles.dangerButton} onClick={() => onDeleteEntity(n.table, n.id, n.label)}>Remove</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
